import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import HockeyReel from "@/components/HockeyReel";
import CategoryCarouselCard from "@/components/CategoryCarouselCard";
import BundleCard from "@/components/BundleCard";
import SEOHead from "@/components/SEOHead";
import { ArrowRight, Truck, Shield, Paintbrush, Instagram, Facebook, Tag, Trophy } from "lucide-react";
import TikTokIcon from "@/components/TikTokIcon";
import { useActiveCompetition } from "@/hooks/useActiveCompetition";
import { useSeasonSale } from "@/hooks/useSeasonSale";

const HERO_BG = "/api/uploads/wgpuabfv_DSC03188.webp";
const ABOUT_US_IMG = "/api/uploads/wfkl70dr_group_shot.webp";

// Rugby: full studio shoot + heritage + mom rugby product shots
const RUGBY_IMAGES = [
  "/api/uploads/t841hopo_DSC03183.webp",
  "/api/uploads/wfkl70dr_group_shot.webp",
  "/api/uploads/273kj20s_DSC03192.webp",
  "/api/uploads/742a5lf9_DSC03195.webp",
  "/api/uploads/0i63fpxf_DSC03167.webp",
  "/api/uploads/heritage_rugby_portrait.webp",
];

// Hockey: best girls + boys shots — sticks/goals allowed here
const HOCKEY_IMAGES = [
  "/api/uploads/hockey_girls_trio_group_v2.webp",
  "/api/uploads/boys_hockey_sitting_portrait.webp",
  "/api/uploads/hockey_girls_bent_playing.webp",
  "/api/uploads/boys_hockey_shorts_lion_front.webp",
  "/api/uploads/hockey_girls_sunset_by_goal.webp",
];

// Netball: studio poses only — NO hockey sticks, NO goal boxes, NO hockey socks/shin guards
const NETBALL_IMAGES = [
  "/api/uploads/hockey_pink_green_arms_crossed.webp",
  "/api/uploads/hockey_pink_green_backtoback.webp",
  "/api/uploads/hockey_pink_green_playful.webp",
  "/api/uploads/hockey_pink_green_side_pose.webp",
];

const SCRUM_CAP_IMAGES = [
  "/api/uploads/scrum_royal_blue.webp",
  "/api/uploads/scrum_rwb_camo.webp",
  "/api/uploads/scrum_pink_camo_detail.webp",
  "/api/uploads/scrum_blue_camo.webp",
];

const TEAM_KIT_IMAGES = [
  "/api/uploads/wfkl70dr_group_shot.webp",
  "/api/uploads/hockey_kit_trio_group.webp",
  "/api/uploads/t841hopo_DSC03183.webp",
];

const SPORT_LINKS = [
  { label: "Rugby", to: "/shop/rugby", testId: "sport-strip-rugby" },
  { label: "Hockey", to: "/shop/hockey", testId: "sport-strip-hockey" },
  { label: "Netball", to: "/shop/netball", testId: "sport-strip-netball" },
  { label: "Training & Equipment", to: "/shop/training", testId: "sport-strip-training" },
  { label: "Team Kits", to: "/bulk-orders", testId: "sport-strip-team-kits" },
];

// Orbit Sports equipment lives across several category tabs — identify by subcategory/category
const EQUIPMENT_SUBS = ["Rugby Balls", "Kicking", "Mouthguards & Protection", "Netballs", "Training Equipment", "Speed & Agility", "Cones & Markers", "Coaching Equipment", "Protection"];

export default function Home() {
  const { isLive: compLive, prizeAmount } = useActiveCompetition();
  const { isActive: saleLive, headline: saleHeadline } = useSeasonSale();
  const [specials, setSpecials] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [galleryImgs, setGalleryImgs] = useState([]);
  const [featuredBundles, setFeaturedBundles] = useState([]);

  useEffect(() => {
    api.get("/products?is_special=true").then((r) => setSpecials(r.data)).catch(() => {});
    api.get("/products").then((r) => setAllProducts(r.data)).catch(() => {});
    api.get("/gallery").then((r) => setGalleryImgs(r.data.slice(0, 10))).catch(() => {});
    api.get("/bundles?featured=true").then((r) => setFeaturedBundles(r.data)).catch(() => {});
  }, []);

  // Fan Favourites: a balanced mix — 2 per sport, 8 total
  const favourites = useMemo(() => {
    const picked = [];
    for (const cat of ["rugby", "hockey", "netball", "scrum-caps"]) {
      const matches = allProducts.filter(
        (p) => (p.categories?.includes(cat) || p.category === cat) && !picked.some((x) => x.id === p.id)
      );
      picked.push(...matches.slice(0, 2));
    }
    return picked.slice(0, 8);
  }, [allProducts]);

  // New Equipment: newest active Orbit Sports equipment across all categories
  // Excludes pre-existing apparel-adjacent protection (scrum caps / shoulder pads) — this strip is the new Orbit range only.
  // Diversify by subcategory first so the grid showcases the range (balls, cones, mouthguards, ladders...) rather than 4 of one type.
  const equipment = useMemo(() => {
    const list = allProducts.filter(
      (p) =>
        (p.category === "training" || EQUIPMENT_SUBS.includes(p.subcategory)) &&
        !/shoulder ?pad|scrum ?cap/i.test(p.name || "")
    );
    const sorted = [...list].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    const firstOfEach = [];
    const rest = [];
    const seen = new Set();
    for (const p of sorted) {
      const key = p.subcategory || p.category;
      if (seen.has(key)) rest.push(p);
      else { seen.add(key); firstOfEach.push(p); }
    }
    return [...firstOfEach, ...rest].slice(0, 8);
  }, [allProducts]);

  // Images for the Training & Equipment category card — real equipment photos, fallback to scrum caps
  const equipmentCardImages = useMemo(() => {
    const imgs = equipment.map((p) => p.image_url).filter(Boolean).slice(0, 4);
    return imgs.length >= 2 ? imgs : SCRUM_CAP_IMAGES;
  }, [equipment]);

  return (
    <div data-testid="home-page">
      <SEOHead
        absoluteTitle="Untamed Gear | Sportswear, Equipment & Performance Gear South Africa"
        description="Untamed Gear — South Africa's home for sportswear, sports equipment and performance gear. Rugby, hockey, netball & training: shorts, jerseys, dresses, scrum caps, rugby & netball balls, cones, agility ladders and more. Locally made apparel + trusted equipment brands."
        keywords="sportswear, sports equipment, rugby balls, netball balls, agility ladder, training cones, rugby shorts, hockey dress, netball skort, scrum caps, South Africa, untamed gear"
        canonicalPath="/"
      />
      {/* Competition banner — visible only while a competition is live */}
      {compLive && (
        <Link to="/competition" data-testid="home-competition-banner"
          className="block bg-[#F7931E] text-black hover:bg-white transition-colors">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-center gap-2 sm:gap-3 text-center">
            <Trophy size={18} className="shrink-0" />
            <span className="font-black uppercase tracking-wider text-sm sm:text-base">
              Win {prizeAmount} Untamed Gear credit — free to enter
            </span>
            <span className="hidden sm:inline-flex items-center gap-1 font-bold uppercase tracking-wider text-xs border border-black/40 px-2 py-1">
              Enter now <ArrowRight size={13} />
            </span>
          </div>
        </Link>
      )}
      {/* Hero */}
      <section className="relative min-h-[70vh] sm:min-h-[80vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="Untamed Gear bold South African sportswear athlete" className="w-full h-full object-cover" fetchPriority="high" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-black/60 to-black/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-24 w-full">
          <div className="max-w-2xl animate-fade-in-up">
            <p className="text-[#F7931E] text-sm font-bold tracking-[0.3em] uppercase mb-4 font-heading">Sportswear • Equipment • Performance</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter uppercase text-white leading-[0.95] mb-6 font-heading">
              Unleash<br />The Untamed
            </h1>
            <p className="text-[#A3A3A3] text-base sm:text-lg mb-8 max-w-lg leading-relaxed">
              Bold sportswear, sports equipment and training gear for Rugby, Hockey &amp; Netball. Locally-made apparel plus trusted equipment brands — built to perform, made to stand out.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/shop" data-testid="hero-shop-btn"
                className="inline-flex items-center gap-2 bg-[#F7931E] text-black font-bold uppercase tracking-wide py-3 px-8 hover:bg-[#D97715] transition-colors btn-press">
                Shop Now <ArrowRight size={18} />
              </Link>
              <Link to="/bulk-orders" data-testid="hero-team-btn"
                className="inline-flex items-center gap-2 border border-[#525252] text-white font-bold uppercase tracking-wide py-3 px-8 hover:bg-[#171717] hover:border-white transition-colors btn-press">
                Team &amp; Bulk Orders
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Sport Discovery Strip */}
      <section data-testid="sport-discovery-strip" className="bg-[#0A0A0A] border-b border-[#262626]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-3 overflow-x-auto no-scrollbar py-4 lg:justify-center">
            {SPORT_LINKS.map((s) => (
              <Link key={s.to + s.label} to={s.to} data-testid={s.testId}
                className="shrink-0 border border-[#525252] text-white font-bold uppercase tracking-wider text-sm px-6 py-2.5 hover:border-[#F7931E] hover:text-[#F7931E] transition-colors btn-press">
                {s.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Free Shipping Banner */}
      <section className="bg-[#F7931E] py-3">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-center gap-2">
          <Truck size={18} className="text-black" />
          <p className="text-black font-bold text-sm uppercase tracking-wider font-heading">
            Free Shipping on orders above R1,000
          </p>
        </div>
      </section>

      {/* End-of-Season Sale promo — only while the sale is active and in its date window */}
      {saleLive && (() => {
        const saleKit = featuredBundles.find((b) => b.bundle_type === "kit");
        const kitPrice = Number(saleKit?.bundle_price ?? 0);
        const kitWas = Number(saleKit?.compare_at_price ?? saleKit?.max_normal_total ?? 0);
        const kitSave = Math.max(0, kitWas - kitPrice);
        return (
        <section data-testid="home-sale-banner" className="bg-black border-y-4 border-[#F7931E] py-10 sm:py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 items-center">
            <div>
              <p className="text-[#F7931E] text-xs sm:text-sm font-bold tracking-[0.3em] uppercase mb-2 font-heading">End-of-Season Sale</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter uppercase text-white font-heading leading-none">
                Up To 30% Off
              </h2>
              <p className="text-white/90 text-sm sm:text-base mt-3 font-bold uppercase tracking-wider">
                Complete Rugby Kit — 1 Jersey + 1 Short
              </p>
              <p className="mt-2 flex flex-wrap items-end gap-3">
                {kitPrice > 0 && <span className="text-[#F7931E] font-black text-4xl">R{kitPrice.toFixed(0)}</span>}
                {kitWas > kitPrice && <span className="text-[#737373] line-through text-lg">Was R{kitWas.toFixed(0)}</span>}
                {kitSave > 0 && <span className="text-green-400 font-bold text-sm mb-1">Save R{kitSave.toFixed(0)}</span>}
              </p>
              {saleHeadline && <p className="text-[#A3A3A3] text-sm mt-3">{saleHeadline}</p>}
            </div>
            <Link to="/sale" data-testid="home-sale-cta"
              className="inline-flex items-center justify-center gap-2 bg-[#F7931E] text-black font-black uppercase tracking-wide py-4 px-10 hover:bg-white transition-colors btn-press text-lg whitespace-nowrap">
              Shop Sale <ArrowRight size={18} />
            </Link>
          </div>
        </section>
        );
      })()}

      {/* Specials Spotlight — only when genuine specials exist */}
      {specials.length > 0 && (
        <section data-testid="specials-spotlight" className="bg-[#0A0A0A] border-t-4 border-[#F7931E] py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
              <div>
                <p className="text-[#F7931E] text-sm font-bold tracking-[0.3em] uppercase mb-2 font-heading flex items-center gap-2">
                  <Tag size={16} /> Hot Deals
                </p>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tighter uppercase text-white font-heading">Specials</h2>
                <p className="text-[#A3A3A3] text-sm mt-2">While stocks last.</p>
              </div>
              <Link to="/shop?specials=true" data-testid="specials-shop-all"
                className="inline-flex items-center gap-2 bg-[#F7931E] text-black font-bold uppercase tracking-wide text-sm px-6 py-3 hover:bg-[#D97715] transition-colors btn-press">
                Shop All Specials <ArrowRight size={16} />
              </Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {specials.slice(0, 4).map((p) => (
                <Link key={p.id} to={`/product/${p.id}`} data-testid={`special-card-${p.id}`}
                  className="group block bg-[#171717] border border-[#262626] hover:border-[#F7931E] transition-colors overflow-hidden">
                  <div className="relative aspect-[3/4] overflow-hidden bg-[#0A0A0A]">
                    <img src={p.image_url} alt={p.name} loading="lazy" decoding="async"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    {p.special_price && (
                      <span className="absolute top-3 left-3 bg-[#F7931E] text-black text-xs font-black uppercase tracking-wider px-3 py-1">
                        Save R{Math.round(p.price - p.special_price)}
                      </span>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="text-white font-bold uppercase tracking-tight text-sm mb-2 font-heading line-clamp-1">{p.name}</h3>
                    <div className="flex items-baseline gap-2">
                      <span className="text-[#F7931E] font-black text-lg">R{(p.special_price || p.price).toFixed(2)}</span>
                      {p.special_price && (
                        <span className="text-[#525252] line-through text-sm">R{p.price.toFixed(2)}</span>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Bundles & Save — featured bundles only */}
      {featuredBundles.length > 0 && (
        <section data-testid="home-bundles-section" className="bg-[#0A0A0A] py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
              <div>
                <p className="text-[#F7931E] text-sm font-bold tracking-[0.3em] uppercase mb-2 font-heading">Build Your Kit. Save More.</p>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tighter uppercase text-white font-heading">Bundles &amp; Save</h2>
                <p className="text-[#A3A3A3] text-sm mt-2">Pair your gear together and pay less.</p>
              </div>
              <Link to="/deals" data-testid="home-bundles-see-all"
                className="inline-flex items-center gap-2 bg-[#F7931E] text-black font-bold uppercase tracking-wide text-sm px-6 py-3 hover:bg-[#D97715] transition-colors btn-press">
                See All Deals <ArrowRight size={16} />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredBundles.slice(0, 3).map((b) => <BundleCard key={b.id} bundle={b} />)}
            </div>
          </div>
        </section>
      )}

      {/* Sport Category Cards — equal importance, horizontal swipe on mobile */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <p className="text-[#F7931E] text-sm font-bold tracking-[0.2em] uppercase mb-2 font-heading">Categories</p>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight uppercase text-white mb-8 font-heading">
          Choose Your Sport
        </h2>
        <div className="flex gap-4 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-2 lg:grid lg:grid-cols-5 lg:overflow-visible lg:pb-0">
          <div className="shrink-0 w-[70vw] sm:w-[40vw] lg:w-auto snap-start">
            <CategoryCarouselCard to="/shop/rugby" label="Rugby" testId="cat-rugby" size="equal" images={RUGBY_IMAGES} intervalMs={4000} />
          </div>
          <div className="shrink-0 w-[70vw] sm:w-[40vw] lg:w-auto snap-start">
            <CategoryCarouselCard to="/shop/hockey" label="Hockey" testId="cat-hockey" size="equal" images={HOCKEY_IMAGES} intervalMs={3600} />
          </div>
          <div className="shrink-0 w-[70vw] sm:w-[40vw] lg:w-auto snap-start">
            <CategoryCarouselCard to="/shop/netball" label="Netball" testId="cat-netball" size="equal" images={NETBALL_IMAGES} intervalMs={4200} />
          </div>
          <div className="shrink-0 w-[70vw] sm:w-[40vw] lg:w-auto snap-start">
            <CategoryCarouselCard to="/shop/training" label="Training & Equipment" testId="cat-training" size="equal" images={equipmentCardImages} intervalMs={3800} eyebrow="New" />
          </div>
          <div className="shrink-0 w-[70vw] sm:w-[40vw] lg:w-auto snap-start">
            <CategoryCarouselCard to="/bulk-orders" label="Team Kits" testId="cat-team-kits" size="equal" images={TEAM_KIT_IMAGES} intervalMs={4400} eyebrow="Custom Orders" />
          </div>
        </div>
      </section>

      {/* New Equipment — Orbit Sports gear pulled live from the catalog */}
      {equipment.length > 0 && (
        <section data-testid="new-equipment-section" className="bg-[#0A0A0A] border-t-4 border-[#F7931E] py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
              <div>
                <p className="text-[#F7931E] text-sm font-bold tracking-[0.3em] uppercase mb-2 font-heading">Now In Stock</p>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tighter uppercase text-white font-heading">New Equipment</h2>
                <p className="text-[#A3A3A3] text-sm mt-2 max-w-lg">Rugby &amp; netball balls, mouthguards, cones, markers, agility ladders and more — quality training equipment to go with your kit.</p>
              </div>
              <Link to="/shop/training" data-testid="new-equipment-shop-all"
                className="inline-flex items-center gap-2 bg-[#F7931E] text-black font-bold uppercase tracking-wide text-sm px-6 py-3 hover:bg-[#D97715] transition-colors btn-press">
                Shop Equipment <ArrowRight size={16} />
              </Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 stagger-children">
              {equipment.slice(0, 4).map((p) => (
                <div key={p.id} className="animate-fade-in-up opacity-0">
                  <ProductCard product={p} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Hockey Reel — real players, real matches */}
      <section data-testid="home-hockey-reel-section" className="bg-[#0A0A0A] border-y border-[#262626] py-14 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 sm:gap-12 items-center">
            <div className="lg:col-span-3 order-2 lg:order-1">
              <p className="text-[#F7931E] text-sm font-bold tracking-[0.3em] uppercase mb-3 font-heading">
                Watch The Reel
              </p>
              <h2 className="text-white text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter uppercase leading-[0.95] mb-5 font-heading">
                Untamed Gear<br />on the astro.
              </h2>
              <p className="text-[#A3A3A3] text-base sm:text-lg leading-relaxed mb-6 max-w-xl">
                Real players. Real matches. Real energy. Watch the girls light up
                the pitch in the full Untamed Gear hockey kit — sublimated,
                4-way-stretch, built to move fast and stay bold.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/shop/hockey"
                  data-testid="home-reel-shop-hockey"
                  className="inline-flex items-center gap-2 bg-[#F7931E] text-black font-bold uppercase tracking-wide text-sm px-6 py-3 hover:bg-[#D97715] transition-colors btn-press"
                >
                  Shop Hockey <ArrowRight size={16} />
                </Link>
                <Link
                  to="/gallery"
                  className="inline-flex items-center gap-2 border border-[#525252] text-white font-bold uppercase tracking-wide text-sm px-6 py-3 hover:bg-[#171717] transition-colors btn-press"
                >
                  View Gallery
                </Link>
              </div>
            </div>
            <div className="lg:col-span-2 order-1 lg:order-2 flex justify-center">
              <div className="w-full max-w-[320px] aspect-[9/16]">
                <HockeyReel
                  src="/api/uploads/videos/hockey_reel.mp4"
                  poster="/api/uploads/videos/hockey_reel_poster.webp"
                  className="w-full h-full"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fan Favourites — balanced mix across all sports */}
      {favourites.length > 0 && (
        <section data-testid="fan-favourites" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="text-[#F7931E] text-sm font-bold tracking-[0.2em] uppercase mb-2 font-heading">Our Gear</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight uppercase text-white font-heading">Fan Favourites</h2>
            </div>
            <Link to="/shop" className="text-[#F7931E] font-bold text-sm uppercase tracking-wider hover:underline flex items-center gap-1">
              Shop All <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 stagger-children">
            {favourites.map((p) => (
              <div key={p.id} className="animate-fade-in-up opacity-0">
                <ProductCard product={p} />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Athlete Gallery Strip — real Untamed Gear photography */}
      {galleryImgs.length > 0 && (
        <section data-testid="athlete-gallery-strip" className="bg-[#0A0A0A] border-y border-[#262626] py-14 sm:py-16 overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8 flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-[#F7931E] text-sm font-bold tracking-[0.3em] uppercase mb-2 font-heading">The Pride</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tighter uppercase text-white font-heading">
                Worn by players across South Africa
              </h2>
            </div>
            <Link to="/gallery" data-testid="athlete-strip-gallery-cta"
              className="inline-flex items-center gap-2 border border-[#525252] text-white font-bold uppercase tracking-wide text-sm px-6 py-3 hover:border-[#F7931E] hover:text-[#F7931E] transition-colors btn-press">
              View Gallery <ArrowRight size={16} />
            </Link>
          </div>
          <div className="flex gap-3 overflow-x-auto no-scrollbar snap-x px-4 sm:px-6 lg:px-8">
            {galleryImgs.map((img, i) => (
              <Link key={img.src + i} to="/gallery" className="shrink-0 snap-start w-[58vw] sm:w-[280px] aspect-[3/4] overflow-hidden border border-[#262626] group">
                <img src={img.src} alt={img.alt || "Untamed Gear athlete"} loading="lazy" decoding="async"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Team / Bulk CTA */}
      <section data-testid="team-bulk-cta" className="bg-[#F7931E] py-14 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div>
            <h2 className="text-black text-2xl sm:text-3xl lg:text-4xl font-black tracking-tighter uppercase font-heading mb-2">
              Kit your team the Untamed way
            </h2>
            <p className="text-black/80 text-base font-medium max-w-xl">
              Custom colours, names, numbers &amp; branding for schools, clubs and teams.
            </p>
          </div>
          <Link to="/bulk-orders" data-testid="team-cta-quote-btn"
            className="shrink-0 inline-flex items-center gap-2 bg-black text-white font-bold uppercase tracking-wide px-8 py-4 hover:bg-[#171717] transition-colors btn-press">
            Get a Team Quote <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* About Us */}
      <section className="bg-[#171717] py-16 sm:py-24" id="about">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#F7931E] text-sm font-bold tracking-[0.2em] uppercase mb-2 font-heading">About Us</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight uppercase text-white mb-6 font-heading">
                Untamed Sports Gear
              </h2>
              <p className="text-[#A3A3A3] text-base leading-relaxed mb-4">
                Untamed Gear is a proudly South African brand focused on delivering high-quality, performance-driven sportswear for athletes who play with passion and purpose.
              </p>
              <p className="text-[#A3A3A3] text-base leading-relaxed mb-4">
                We design and manufacture our own sublimated rugby, netball and hockey apparel — bold, durable and comfortable, built for both training and match-day performance. Alongside our kit, we stock a growing range of trusted sports equipment so you can gear up for the whole season in one place.
              </p>
              <p className="text-[#A3A3A3] text-base leading-relaxed mb-4">
                At Untamed Gear, we believe in empowering players to express themselves through strong, unique designs while ensuring top-level functionality on the field. Whether you're part of a team or an individual athlete, you can order exactly what you need.
              </p>
              <p className="text-[#A3A3A3] text-base leading-relaxed mb-8">
                All our apparel is made in South Africa using premium fabrics to ensure long-lasting quality and comfort, and our equipment is carefully selected from brands we trust.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="https://www.instagram.com/untamedsportsgear" target="_blank" rel="noopener noreferrer"
                  data-testid="about-instagram" className="flex items-center gap-2 bg-[#262626] text-white px-5 py-2 hover:bg-[#F7931E] hover:text-black transition-colors btn-press">
                  <Instagram size={18} /> Instagram
                </a>
                <a href="https://www.facebook.com/share/1GHfSftdJ4/" target="_blank" rel="noopener noreferrer"
                  data-testid="about-facebook" className="flex items-center gap-2 bg-[#262626] text-white px-5 py-2 hover:bg-[#F7931E] hover:text-black transition-colors btn-press">
                  <Facebook size={18} /> Facebook
                </a>
                <a href="https://www.tiktok.com/@untamed.gear" target="_blank" rel="noopener noreferrer"
                  data-testid="about-tiktok" className="flex items-center gap-2 bg-[#262626] text-white px-5 py-2 hover:bg-[#F7931E] hover:text-black transition-colors btn-press">
                  <TikTokIcon size={18} /> TikTok
                </a>
              </div>
            </div>
            <div className="flex justify-center">
              <img src={ABOUT_US_IMG} alt="Untamed Gear rugby team group shot" data-testid="about-us-image" className="w-full max-w-md object-cover border border-[#262626]" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* Brand Values */}
      <section className="border-t border-[#262626] py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="flex flex-col items-center text-center p-6">
              <Shield size={40} className="text-[#F7931E] mb-4" />
              <h3 className="text-white font-bold uppercase tracking-tight text-lg mb-2 font-heading">Built to Last</h3>
              <p className="text-[#A3A3A3] text-sm">Premium fabrics and reinforced stitching. Untamed Gear apparel is locally made in South Africa.</p>
            </div>
            <div className="flex flex-col items-center text-center p-6">
              <Paintbrush size={40} className="text-[#F7931E] mb-4" />
              <h3 className="text-white font-bold uppercase tracking-tight text-lg mb-2 font-heading">Fearless Designs</h3>
              <p className="text-[#A3A3A3] text-sm">Stand out with eye-catching sublimated designs. No minimum order — team &amp; bulk orders also available.</p>
            </div>
            <div className="flex flex-col items-center text-center p-6">
              <Truck size={40} className="text-[#F7931E] mb-4" />
              <h3 className="text-white font-bold uppercase tracking-tight text-lg mb-2 font-heading">SA Delivery</h3>
              <p className="text-[#A3A3A3] text-sm">Fast, reliable delivery across South Africa. Free shipping on orders above R1,000.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
